import { pageSuspense, useSuspenseLoader } from "@/helpers/suspense";
import { LocationHook, RouteHook, useLocation, useRoute } from "preact-iso";
import { ProductPage } from "../product-page/ProductPage";
import { CategoryPage } from "../category-page";
import { BrandPage } from "../brand-page";
import { NotFoundPage } from "../not-found-page/NotFoundPage";
import { CatalogLayout } from "@/elements-react/catalog-layout";
import { useServices } from "@/services-context";
import { Url } from "@/helpers/url";
import { Services } from "@/services/services";
import { RegistryItem } from "@/services/routes";
import { CatalogPageStore } from "../catalog-page-common/page-store";
import { useEffect, useMemo } from "preact/hooks";
import { useStoreSync } from "@/elements-react/product-area/store/sync";
import { ProductPageStore } from "../product-page/ProductPageCommon";
import { NotFoundPageStore } from "../not-found-page/not-found-page-store";

type Page = {
    type: 'category'
    registryItem: RegistryItem;
    store: CatalogPageStore;
} | {
    type: 'brand'
    registryItem: RegistryItem;
    store: CatalogPageStore;
} | {
    type: 'product'
    registryItem: RegistryItem
    store: ProductPageStore;
} | {
    type: 'not-found'
    store: NotFoundPageStore;
};

const renderPageContent = (page: Page, suspenseKey: string, route: RouteHook, location: LocationHook) => {
    if (page.type === "product") return <ProductPage store={page.store} />;
    if (page.type === 'category') return <CategoryPage key={suspenseKey} location={location} route={route} store={page.store} />;
    if (page.type === 'brand') return <BrandPage key={suspenseKey} location={location} route={route} store={page.store} />;
    if (page.type === 'not-found') return <NotFoundPage />;
};

const createSuspenseKey = (path: string, date = new Date()) => `dynamic-route-${path}-${date.getTime()}`;

export class DynamicRouteStore {
    target: null | RegistryItem;
    page: null | Page;

    constructor(protected services: Services) {
        this.target = null;
        this.page = null;
    }

    load = async (location: LocationHook, route: RouteHook) => {
        await this.fetchTarget(route);
        await this.fetchPageStore(location, route);
    }

    update = async (location: LocationHook, route: RouteHook) => {
        this.updatePageStore(this.page!, location, route);
    }

    fetchTarget = async (route: RouteHook) => {
        this.target = await this.services.routes.resolve(Url.normalizeRoute(route.path), route.params.lang) as RegistryItem;
    }

    fetchPageStore = async (location: LocationHook, route: RouteHook) => {
        this.page = this.createPageStore(this.target);

        if (this.page.store instanceof CatalogPageStore || this.page.store instanceof ProductPageStore) {
            await this.page.store.loadForLocation({ location, route });
        }
    }
    createPageStore = (target: null | RegistryItem): Page => {
        if (target?.type === 'category' || target?.type === 'brand') {
            const store = new CatalogPageStore(this.services);
            store.setContext({ [target.type]: target.id as string });

            return {
                type: target.type,
                registryItem: target,
                store: store,
            };
        } else if (target?.type === 'product') {
            return {
                type: 'product',
                registryItem: target,
                store: new ProductPageStore(this.services),
            };
        } else {
            return { type: 'not-found', store: new NotFoundPageStore() };
        }
    }

    updatePageStore = (page: Page, location: LocationHook, route: RouteHook) => {

    }

    get sync() {
        return this.page!.store.sync;
    }
}

export const DynamicRoute = pageSuspense(() => {
    const route = useRoute();
    const location = useLocation();
    const key = useMemo(() => createSuspenseKey(route.path), [route.path]);

    return <DynamicRouteInner suspenseKey={key} location={location} route={route} />;
});

const DynamicRouteInner = (props: { suspenseKey: string, location: LocationHook, route: RouteHook }) => {
    const { location, route, suspenseKey } = props;
    const services = useServices();

    const store = useSuspenseLoader({
        key: suspenseKey,
        loader: async () => {
            await services.initialize.prepare({
                location: location,
                route: route,
            });

            const store = new DynamicRouteStore(services);

            await store.load(location, route);

            return store;
        },
    });

    useEffect(() => {
        store.update(location, route);
    }, [location, route, store]);

    useStoreSync(store.sync);

    return <CatalogLayout>{renderPageContent(store.page!, suspenseKey, route, location)}</CatalogLayout>;
};